import { useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import { heroSlides, movies } from "@/mocks/movies";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const total = heroSlides.length;

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [total]);

  const goTo = (next: number) => {
    setIndex((next + total) % total);
  };

  const handleTouchStart = (event: TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      goTo(delta > 0 ? index - 1 : index + 1);
    }
    touchStartX.current = null;
  };

  const slide = heroSlides[index];
  const movie = movies.find((item) => item.id === slide.movieId);

  return (
    <section
      className="relative w-full h-[520px] md:h-[640px] overflow-hidden bg-background-100"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {heroSlides.map((item, itemIndex) => (
        <div
          key={item.movieId}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            itemIndex === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.image}
            alt={`${item.tagline} 焦点影片海报`}
            title="日韩黄色电影精选影片"
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/50" />

      <div className="relative z-10 w-full h-full px-4 md:px-6 flex items-center">
        <div className="w-full max-w-[1400px] mx-auto">
          <div className="max-w-[620px]">
            <div className="flex items-center gap-3 fade-in" key={`tag-${index}`}>
              <span className="h-px w-8 bg-accent-500" />
              <span className="text-[11px] md:text-xs tracking-[0.26em] uppercase text-accent-500 font-label">
                {slide.tagline}
              </span>
            </div>

            <h1
              className="mt-5 font-heading text-4xl md:text-6xl leading-[1.1] text-foreground-950 fade-up"
              key={`title-${index}`}
            >
              {movie?.title ?? "日韩黄色电影"}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-star-fill text-accent-500" />
                {movie?.rating.toFixed(1)}
              </span>
              <span className="w-px h-3 bg-background-400/60" />
              <span>{movie?.year}</span>
              <span className="w-px h-3 bg-background-400/60" />
              <span>{movie?.region}</span>
              <span className="w-px h-3 bg-background-400/60" />
              <span>{movie?.duration} 分钟</span>
              <span className="w-px h-3 bg-background-400/60" />
              <span>{movie?.lengthType}</span>
            </div>

            <p className="mt-5 text-sm md:text-[15px] leading-relaxed text-foreground-600 line-clamp-3 max-w-[520px]">
              {movie?.synopsis}
            </p>

            <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link
                to={`/detail/${slide.movieId}`}
                className="h-11 px-6 rounded-md bg-primary-500 text-foreground-950 text-sm flex items-center justify-center gap-2 cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-lg" />
                查看影片详情
              </Link>
              <Link
                to="/category/recent"
                className="h-11 px-6 rounded-md border border-background-400/60 text-foreground-800 text-sm flex items-center justify-center gap-2 cursor-pointer hover:border-accent-500 hover:text-accent-500 transition-colors whitespace-nowrap"
              >
                浏览近期新片
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute z-20 bottom-5 md:bottom-8 left-0 w-full px-4 md:px-6">
        <div className="w-full max-w-[1400px] mx-auto flex items-end justify-between gap-4">
          <div className="flex items-center gap-2">
            {heroSlides.map((item, itemIndex) => (
              <button
                key={item.movieId}
                type="button"
                onClick={() => goTo(itemIndex)}
                aria-label={`切换到第 ${itemIndex + 1} 张焦点图`}
                className={`h-1 rounded-full cursor-pointer transition-all duration-300 ${
                  itemIndex === index ? "w-8 bg-accent-500" : "w-4 bg-foreground-500/40 hover:bg-foreground-500"
                }`}
              />
            ))}
            <span className="ml-3 font-label text-[11px] tracking-widest text-foreground-500">
              0{index + 1} / 0{total}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goTo(index - 1)}
              aria-label="上一张"
              className="w-9 h-9 md:w-10 md:h-10 rounded-md border border-background-400/50 flex items-center justify-center text-foreground-700 cursor-pointer hover:border-accent-500 hover:text-accent-500 transition-colors"
            >
              <i className="ri-arrow-left-line" />
            </button>
            <button
              type="button"
              onClick={() => goTo(index + 1)}
              aria-label="下一张"
              className="w-9 h-9 md:w-10 md:h-10 rounded-md border border-background-400/50 flex items-center justify-center text-foreground-700 cursor-pointer hover:border-accent-500 hover:text-accent-500 transition-colors"
            >
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}